// exportEomXlsx — write the generated report out as a formatted .xlsx.
//
// The shared downloadXlsx() helper is flat-table-only (one jade header row +
// uniform data rows), which can't express this report: it has a title block,
// section bands, subtotal emphasis, and mixed money/percent rows. So this
// module drives ExcelJS directly — but pulls its palette and number formats
// from lib/exportXlsx.js so the file looks like every other Atlas export.
//
// Two sheets:
//   'Financial Report'  the report itself — what James asked for
//   'Account Mapping'   every Xero account and the line it fed
//
// The second sheet is the audit trail. It's what turns "trust the numbers"
// into "check the numbers", and it's the difference between a spreadsheet
// someone can sign off and one they have to redo by hand.

import ExcelJS from 'exceljs';
import {
  JADE, HEADER_TEXT, THIN_BORDER, NUM_FMT, downloadWorkbook,
} from '../../lib/exportXlsx.js';
import { REPORT_LINES } from './accountMap.js';

const INK = 'FF0F1419';        // primary text
const MUTED = 'FF5C6166';      // secondary text
const BAND = 'FFF7F6F2';       // section band fill (mercury off-white)
const SUBTOTAL_BG = 'FFECF6F0'; // pale jade for subtotal rows
const WARN_BG = 'FFFEF2F2';    // variance banner
const WARN_INK = 'FFB91C1C';

const SERIF = 'Georgia';       // closest ubiquitous stand-in for Tiempos

/**
 * Construct the workbook. Split out from the download so it can be exercised
 * under plain node (Blob/document don't exist there) — see
 * app/scripts/verify-eom.mjs, which writes this to disk and reads it back to
 * confirm the figures survive the write→read round-trip.
 *
 * @param {object} report  result of buildReport()
 * @returns {ExcelJS.Workbook}
 */
export function buildEomWorkbook(report) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Atlas — First Or Nothing';
  wb.created = new Date();

  addReportSheet(wb, report);
  addMappingSheet(wb, report);
  return wb;
}

/**
 * Build and download the report workbook. Browser-only (uses Blob + an
 * anchor click via downloadWorkbook).
 *
 * @param {object} report  result of buildReport()
 * @returns {Promise<string>} the filename written
 */
export async function downloadEomReport(report) {
  const wb = buildEomWorkbook(report);
  const filename = buildFilename(report);
  await downloadWorkbook(filename, wb);
  return filename;
}

// Safe, sortable filename: FON_Financial_Report_Jun-26.xlsx
export function buildFilename(report) {
  const period = String(report.periodKey || 'report')
    .replace(/[^A-Za-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `FON_Financial_Report_${period}.xlsx`;
}

// ── sheet 1: the report ────────────────────────────────────────────────

function addReportSheet(wb, report) {
  const ws = wb.addWorksheet('Financial Report', {
    views: [{ state: 'frozen', ySplit: 6 }],
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  ws.columns = [
    { width: 40 },  // A — line label
    { width: 18 },  // B — value
    { width: 46 },  // C — contributing accounts / notes
  ];

  // ── title block ──
  const org = ws.addRow([report.meta?.orgName || 'First Or Nothing']);
  org.getCell(1).font = { name: SERIF, size: 15, color: { argb: INK } };
  ws.mergeCells(org.number, 1, org.number, 3);

  const title = ws.addRow(['Financial Report']);
  title.getCell(1).font = { name: SERIF, size: 12, bold: true, color: { argb: INK } };
  ws.mergeCells(title.number, 1, title.number, 3);

  const periodRow = ws.addRow([report.meta?.periodLabel || `Period: ${report.periodKey}`]);
  periodRow.getCell(1).font = { size: 10, italic: true, color: { argb: MUTED } };
  ws.mergeCells(periodRow.number, 1, periodRow.number, 3);

  // Provenance line. Uses the title the export actually carried rather than
  // asserting a report type — see readPreamble() in xeroParse.js.
  const src = ws.addRow([
    `Source: Xero ${report.meta?.reportTitle || 'financial export'} · column "${report.periodKey}" · generated ${new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}`,
  ]);
  src.getCell(1).font = { size: 9, color: { argb: MUTED } };
  ws.mergeCells(src.number, 1, src.number, 3);

  ws.addRow([]);

  // ── column header ──
  const head = ws.addRow(['', report.periodKey, 'Contributing Xero accounts']);
  head.height = 20;
  head.eachCell({ includeEmpty: true }, cell => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: JADE } };
    cell.alignment = { vertical: 'middle' };
    cell.border = allBorders();
  });
  head.getCell(2).alignment = { vertical: 'middle', horizontal: 'right' };

  // ── report lines ──
  for (const line of report.lines) {
    if (line.kind === 'header') {
      const r = ws.addRow([line.label.toUpperCase(), '', '']);
      r.eachCell({ includeEmpty: true }, cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BAND } };
        cell.font = { bold: true, size: 9, color: { argb: MUTED } };
        cell.border = allBorders();
      });
      continue;
    }

    const isPct = line.numFmt === 'pct';
    const r = ws.addRow([line.label, line.value, contributorNote(line)]);

    const labelCell = r.getCell(1);
    labelCell.font = { bold: line.emphasis, size: 10, color: { argb: INK } };
    labelCell.alignment = { indent: line.emphasis ? 0 : 1 };

    const valueCell = r.getCell(2);
    // Write real numbers, not formatted strings, so the recipient can keep
    // working in the sheet. numFmt does the presentation.
    valueCell.value = line.value == null ? '—' : line.value;
    valueCell.numFmt = line.value == null ? undefined : (isPct ? NUM_FMT.pct1 : NUM_FMT.money0);
    valueCell.font = { bold: line.emphasis, size: 10, color: { argb: INK } };
    valueCell.alignment = { horizontal: 'right' };

    const noteCell = r.getCell(3);
    noteCell.font = { size: 9, color: { argb: MUTED } };
    noteCell.alignment = { wrapText: false };

    r.eachCell({ includeEmpty: true }, cell => {
      cell.border = allBorders();
      if (line.emphasis) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SUBTOTAL_BG } };
      }
    });
  }

  // ── reconciliation footer ──
  ws.addRow([]);
  const rec = report.reconciliation;
  const tie = ws.addRow(['Reconciliation vs Xero', '', '']);
  tie.getCell(1).font = { bold: true, size: 10, color: { argb: INK } };

  if (rec.status === 'ok') {
    const r = ws.addRow(['Net Profit ties to Xero', rec.xero, 'Checked automatically on generation']);
    r.getCell(2).numFmt = NUM_FMT.money0;
    r.getCell(2).alignment = { horizontal: 'right' };
    r.eachCell({ includeEmpty: true }, c => { c.font = { size: 9, color: { argb: MUTED } }; });
  } else if (rec.status === 'variance') {
    // A mis-mapped month must be visible in the file itself, not just in the
    // UI of whoever generated it. This is the whole point of the check.
    const r1 = ws.addRow(["Xero's Net Profit", rec.xero, '']);
    const r2 = ws.addRow(['This report', rec.computed, '']);
    const r3 = ws.addRow(['Variance', rec.delta, 'REVIEW REQUIRED — an account is unmapped or counted twice']);
    [r1, r2, r3].forEach(r => {
      r.getCell(2).numFmt = NUM_FMT.money2;
      r.getCell(2).alignment = { horizontal: 'right' };
    });
    r3.eachCell({ includeEmpty: true }, cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: WARN_BG } };
      cell.font = { bold: true, size: 9, color: { argb: WARN_INK } };
      cell.border = allBorders();
    });
  } else {
    const r = ws.addRow(['Not checked', '', rec.message]);
    r.eachCell({ includeEmpty: true }, c => { c.font = { size: 9, color: { argb: MUTED } }; });
  }
}

// Short provenance string for a line, e.g. "Shopify Sales, Amazon Sales +1 more".
function contributorNote(line) {
  const cs = line.contributors || [];
  if (!cs.length) {
    return line.kind === 'derived' ? formulaNote(line.key) : '';
  }
  const names = cs.map(c => c.label);
  const shown = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${shown} +${names.length - 3} more` : shown;
}

// Spell out the arithmetic so a reader can check a derived figure by hand
// without opening the code.
function formulaNote(key) {
  const notes = {
    net_sales: 'Sales less Discounts',
    gross_profit: 'Net Sales less Cost of Goods Sold',
    sales_margin: 'Gross Profit ÷ Net Sales',
    total_expenses: 'Advertising & Marketing plus Operating Expenses',
    operating_profit: 'Gross Profit less Total Expenses',
    net_profit: 'Operating Profit',
    net_margin: 'Net Profit ÷ Net Sales',
  };
  return notes[key] || '';
}

// ── sheet 2: account mapping (the audit trail) ─────────────────────────

function addMappingSheet(wb, report) {
  const ws = wb.addWorksheet('Account Mapping', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = [
    { header: 'Xero account', width: 38 },
    { header: 'Xero section', width: 22 },
    { header: 'Mapped to', width: 24 },
    { header: 'Amount', width: 15 },
    { header: 'How', width: 26 },
  ];

  const headerRow = ws.getRow(1);
  headerRow.height = 20;
  headerRow.eachCell(cell => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: JADE } };
    cell.alignment = { vertical: 'middle' };
    cell.border = allBorders();
  });

  const labelFor = (key) => REPORT_LINES.find(l => l.key === key)?.label || key;
  const howFor = (c) => {
    if (c.source === 'override') return 'Assigned by user';
    return c.confidence === 'low' ? 'Catch-all — please confirm' : 'Matched automatically';
  };

  // Mapped accounts, grouped by report line so the sheet reads in report order.
  for (const line of report.lines) {
    for (const c of (line.contributors || [])) {
      const r = ws.addRow([c.label, c.section || '', labelFor(line.key), c.value, howFor(c)]);
      r.getCell(4).numFmt = NUM_FMT.money2;
      if (c.confidence === 'low' && c.source !== 'override') {
        r.getCell(5).font = { color: { argb: WARN_INK }, size: 9, bold: true };
      }
    }
  }

  // Anything the user deliberately dropped — recorded, because an omission
  // is exactly the kind of thing someone needs to see next month.
  for (const e of (report.review?.excluded || [])) {
    const r = ws.addRow([e.label, e.section || '', 'Excluded', e.value, 'Removed by user']);
    r.getCell(4).numFmt = NUM_FMT.money2;
    r.eachCell({ includeEmpty: true }, cell => {
      cell.font = { size: 9, color: { argb: MUTED }, italic: true };
    });
  }

  // Anything the rules couldn't place at all.
  for (const u of (report.review?.unmapped || [])) {
    const r = ws.addRow([u.label, u.section || '', 'NOT MAPPED', u.value, 'Excluded from the report']);
    r.getCell(4).numFmt = NUM_FMT.money2;
    r.eachCell({ includeEmpty: true }, cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: WARN_BG } };
      cell.font = { size: 9, color: { argb: WARN_INK }, bold: true };
    });
  }

  ws.eachRow((row, n) => {
    if (n === 1) return;
    row.eachCell({ includeEmpty: true }, cell => { cell.border = allBorders(); });
  });
}

function allBorders() {
  return { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };
}
